import type { ProgrammaticLandingPageConfig } from '../../types'
import {
  STORAGE_REFERENCE_KWP,
  SELF_CONSUMPTION_WITHOUT_BATTERY,
  SELF_CONSUMPTION_BATTERY_BONUS,
  SELF_CONSUMPTION_WITH_BATTERY,
  BATTERY_COST_PER_KWH_RANGE_EUR,
} from '../storageSizeFacts'

export const KWH_7_BATERIA_SOLAR: ProgrammaticLandingPageConfig = {
  slug: 'bateria-solar-7-kwh-precio',
  lang: 'es',
  topicId: 'kwh-speicher-7',
  title: 'Batería solar de 7 kWh: precio y valores orientativos 2026',
  metaDescription:
    '¿Cuánto cuesta una batería solar de 7 kWh? Para quién es adecuada, valores orientativos de ' +
    'autoconsumo y comparación gratuita de presupuestos para instalación + batería.',
  keywords: [
    'batería solar 7 kWh precio',
    'acumulador solar 7 kWh coste',
    'batería fotovoltaica 7 kWh',
    'cuánto cuesta una batería de 7 kWh',
  ],
  eyebrow: 'Tamaño de batería: 7 kWh',
  h1: 'Batería solar de 7 kWh: precio y valores orientativos',
  intro:
    'Una batería de 7 kWh es un tamaño habitual para hogares medios, combinada con una ' +
    `instalación de referencia de ${STORAGE_REFERENCE_KWP} kWp. Aquí encontrará valores ` +
    'orientativos sobre el tamaño de hogar adecuado, el autoconsumo con batería y justo debajo ' +
    'una calculadora con el precio medio ya calculado para esta combinación a partir de ' +
    'presupuestos reales de empresas instaladoras.',
  calculatorPrefill: { kind: 'kwp+storage', kwp: STORAGE_REFERENCE_KWP, kwh: 7 },
  indexPolicy: 'index',
  sections: [
    { type: 'heading', text: '¿Para quién es adecuada una batería de 7 kWh?' },
    {
      type: 'paragraph',
      text:
        'Valor orientativo: 7 kWh suele encajar con hogares medios de 3-4 personas sin bomba de ' +
        'calor ni coche eléctrico. Quien además cargue parcialmente un coche eléctrico en casa o ' +
        'tenga mayor consumo por la noche encontrará el siguiente tamaño en la página de 12 kWh.',
    },
    { type: 'heading', text: 'Autoconsumo con batería solar' },
    {
      type: 'paragraph',
      text:
        `Sin batería, el autoconsumo de una instalación de ${STORAGE_REFERENCE_KWP} kWp ronda ` +
        `normalmente el ${SELF_CONSUMPTION_WITHOUT_BATTERY * 100} %; con batería solar, unos ` +
        `${SELF_CONSUMPTION_BATTERY_BONUS * 100} puntos más en el modelo de cálculo, hasta rondar ` +
        `el ${SELF_CONSUMPTION_WITH_BATTERY * 100} % - con coche eléctrico o bomba de calor en el ` +
        'hogar, el porcentaje puede seguir subiendo hasta un límite del 85 %. Un cálculo exacto ' +
        'según su consumo lo ofrece la calculadora de dimensionamiento.',
    },
    { type: 'heading', text: 'Suministro de emergencia' },
    {
      type: 'paragraph',
      text:
        'Con 7 kWh, muchas cajas de respaldo para suministro de emergencia ya alcanzan para varios ' +
        'consumidores centrales durante un periodo más largo, aunque no necesariamente para toda ' +
        'la vivienda - depende del consumo real de los aparatos conectados. Si un presupuesto ' +
        'incluye o no esta función depende de cada instalador - conviene preguntarlo directamente.',
    },
    { type: 'heading', text: 'Coste de la batería y ayudas' },
    {
      type: 'paragraph',
      text:
        'El coste de las baterías varía mucho según el fabricante y la complejidad de la ' +
        'instalación - la investigación de mercado de 2026 muestra un rango de entre ' +
        `${BATTERY_COST_PER_KWH_RANGE_EUR.min} y ${BATTERY_COST_PER_KWH_RANGE_EUR.max.toLocaleString('es-ES')} ` +
        '€ por kWh. En Alemania existen ayudas específicas para baterías según la región (p. ej. ' +
        'el programa del estado federado de Berlín, con subvención para baterías); en Austria, el ' +
        'sistema EAG añade 150 €/kWh para el acumulador - un cálculo exacto para su combinación lo ' +
        'ofrece la calculadora de abajo.',
    },
  ],
  faq: [
    {
      q: '¿Es 7 kWh el tamaño adecuado para un hogar estándar?',
      a: 'Para un hogar medio de 3-4 personas sin bomba de calor ni coche eléctrico, 7 kWh es un tamaño habitual. Con consumidores adicionales grandes, conviene valorar 12 o 15 kWh.',
    },
    {
      q: '¿Es suficiente 7 kWh para ser autosuficiente durante la noche?',
      a: 'En muchos casos sí, con un consumo medio sin bomba de calor ni carga de coche eléctrico. Una estimación exacta según su consumo real la ofrece la calculadora de dimensionamiento.',
    },
    {
      q: '¿Con qué tamaño de instalación se combina normalmente una batería de 7 kWh?',
      a: `Aquí se toma como referencia una instalación de ${STORAGE_REFERENCE_KWP} kWp. Con instalaciones algo más pequeñas, 7 kWh también suele encajar bien - la calculadora ofrece una recomendación exacta para su tejado.`,
    },
  ],
  primaryCta: { label: 'Solicitar presupuesto gratis ahora', action: 'customer' },
  secondaryCta: {
    label: 'Coste de una instalación fotovoltaica: guía completa',
    action: 'link',
    href: '/coste-instalacion-fotovoltaica',
  },
  relatedLinks: [
    { label: 'Batería solar de 5 kWh: precio y valores orientativos', href: '/bateria-solar-5-kwh-precio' },
    { label: 'Batería solar de 12 kWh: precio y valores orientativos', href: '/bateria-solar-12-kwh-precio' },
    { label: 'Instalación fotovoltaica de 10 kWp: precio y valores orientativos', href: '/instalacion-fotovoltaica-10-kwp-precio' },
    { label: 'Cómo funciona la comparación de presupuestos', href: '/comparar-presupuestos-fotovoltaicos' },
  ],
}
